import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EditList } from '../../utils/editing/editList';
import { outputDurationMs, segmentPlacements, segmentPlayMs } from '../../utils/editing/editList';

/**
 * Plays an edit list on one <video> without rendering: the element plays the
 * source, and when it reaches the end of a kept stretch it jumps to the next
 * one (or stops at the end of the result). The clock the screen sees is the
 * result's (output time), the same one overlays, text and captions are timed
 * in. Speed changes set the element's playback rate for that stretch; a held
 * frame pauses the element on the stretch's last frame while the clock runs on.
 * A marked stretch can loop, and the whole preview can run slower or faster.
 */

export interface PreviewPlayer {
  /** Callback ref for the <video>. */
  ref: (node: HTMLVideoElement | null) => void;
  element: HTMLVideoElement | null;
  /** Output time, ms. */
  outputMs: number;
  /** The result's length, ms. */
  durationMs: number;
  playing: boolean;
  ready: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Seeks in output time. */
  seek: (outputMs: number) => void;
  /** The element's natural size, once known. */
  videoWidth: number;
  videoHeight: number;
  /** The source time the element is at. */
  sourceMs: number;
  /** The kept stretch playing now (index into the placements). */
  segmentIndex: number;
  /** True while a held frame is showing. */
  holding: boolean;
  /** Preview speed on top of each stretch's own (0.25–2). */
  rate: number;
  setRate: (rate: number) => void;
  /** Plays this stretch of the result over and over; null plays straight through. */
  loop: { fromMs: number; toMs: number } | null;
  setLoop: (loop: { fromMs: number; toMs: number } | null) => void;
}

const TICK_MS = 40;

/** Which stretch an output time falls in (the last one includes its end). */
export const placementIndexAt = (placements: ReturnType<typeof segmentPlacements>, outputMs: number): number => {
  for (let i = 0; i < placements.length; i += 1) {
    const { startMs, endMs } = placements[i];
    if (outputMs >= startMs && (outputMs < endMs || (i === placements.length - 1 && outputMs <= endMs))) return i;
  }
  return outputMs < 0 ? 0 : placements.length - 1;
};

export function usePreviewPlayer(list: EditList): PreviewPlayer {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const [outputMs, setOutputMs] = useState(0);
  const [sourceMs, setSourceMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [holding, setHolding] = useState(false);
  const [rate, setRateState] = useState(1);
  const [loop, setLoopState] = useState<{ fromMs: number; toMs: number } | null>(null);
  const placements = useMemo(() => segmentPlacements(list), [list]);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const durationMs = useMemo(() => outputDurationMs(list), [list]);
  /** The stretch the element is meant to be in. */
  const indexRef = useRef(0);
  const outputRef = useRef(0);
  const seekingRef = useRef(false);
  const rateRef = useRef(1);
  const loopRef = useRef<{ fromMs: number; toMs: number } | null>(null);
  /** While a frame is held: when the hold began on the wall clock, and the output time it began at. */
  const holdRef = useRef<{ startedAt: number; fromMs: number } | null>(null);
  /** "Playing" as the creator sees it: during a hold the element is paused but the preview plays on. */
  const wantPlayRef = useRef(false);

  const ref = useCallback((node: HTMLVideoElement | null) => setElement(node), []);

  const goTo = useCallback((target: number, el: HTMLVideoElement) => {
    const all = placementsRef.current;
    const total = all.length ? all[all.length - 1].endMs : 0;
    const clamped = Math.max(0, Math.min(total, target));
    const index = placementIndexAt(all, clamped);
    const { segment, startMs } = all[index];
    const local = clamped - startMs;
    const playMs = segmentPlayMs(segment);
    indexRef.current = index;
    outputRef.current = clamped;
    seekingRef.current = true;
    setSegmentIndex(index);
    el.playbackRate = segment.speed * rateRef.current;
    if (local >= playMs && segment.holdMs > 0) {
      // Inside a held frame: show the last frame, and let the clock run from here.
      el.currentTime = Math.max(segment.inMs, segment.outMs - 1) / 1000;
      holdRef.current = { startedAt: performance.now(), fromMs: clamped };
      setHolding(true);
      if (!el.paused) el.pause();
    } else {
      holdRef.current = null;
      setHolding(false);
      el.currentTime = (segment.inMs + local * segment.speed) / 1000;
      if (wantPlayRef.current && el.paused) {
        const result = el.play() as Promise<void> | undefined;
        if (result && typeof result.catch === 'function') result.catch(() => undefined);
      }
    }
    setOutputMs(clamped);
  }, []);

  useEffect(() => {
    if (!element) return undefined;
    let frame: number | null = null;
    let lastTick = 0;
    const finish = (endMs: number) => {
      wantPlayRef.current = false;
      holdRef.current = null;
      setHolding(false);
      element.pause();
      setPlaying(false);
      outputRef.current = endMs;
      setOutputMs(endMs);
    };
    const tick = (now: number) => {
      frame = window.requestAnimationFrame(tick);
      if (now - lastTick < TICK_MS) return;
      lastTick = now;
      const all = placementsRef.current;
      if (all.length === 0) return;
      const index = Math.min(indexRef.current, all.length - 1);
      const { segment, startMs, endMs } = all[index];
      const looping = loopRef.current;

      // A held frame: the clock runs on the wall clock, the picture stays.
      if (holdRef.current) {
        if (!wantPlayRef.current) return;
        const at = holdRef.current.fromMs + (now - holdRef.current.startedAt) * rateRef.current;
        if (looping && at >= looping.toMs) {
          goTo(looping.fromMs, element);
          return;
        }
        if (at < endMs) {
          outputRef.current = at;
          setOutputMs(at);
          return;
        }
        holdRef.current = null;
        setHolding(false);
        if (index + 1 < all.length) goTo(all[index + 1].startMs, element);
        else finish(endMs);
        return;
      }

      if (seekingRef.current || element.seeking) return;
      const currentMs = element.currentTime * 1000;
      setSourceMs(currentMs);
      const at = Math.max(startMs, startMs + (currentMs - segment.inMs) / segment.speed);
      if (looping && wantPlayRef.current && at >= looping.toMs) {
        goTo(looping.fromMs, element);
        return;
      }
      if (currentMs < segment.outMs) {
        outputRef.current = at;
        setOutputMs(at);
        const wanted = segment.speed * rateRef.current;
        if (Math.abs(element.playbackRate - wanted) > 1e-6) element.playbackRate = wanted;
        return;
      }
      // Ran past the end of this stretch's footage: hold its frame, or go on.
      if (segment.holdMs > 0 && wantPlayRef.current) {
        const holdFrom = startMs + segmentPlayMs(segment);
        element.pause();
        holdRef.current = { startedAt: now, fromMs: holdFrom };
        setHolding(true);
        outputRef.current = holdFrom;
        setOutputMs(holdFrom);
        return;
      }
      if (index + 1 < all.length) {
        goTo(all[index + 1].startMs, element);
      } else {
        finish(endMs);
      }
    };
    frame = window.requestAnimationFrame(tick);
    const onSeeked = () => { seekingRef.current = false; };
    const onPlay = () => setPlaying(true);
    const onPause = () => { if (!holdRef.current) setPlaying(false); };
    const onMeta = () => {
      setReady(true);
      setSize({ width: element.videoWidth, height: element.videoHeight });
    };
    element.addEventListener('seeked', onSeeked);
    element.addEventListener('play', onPlay);
    element.addEventListener('pause', onPause);
    element.addEventListener('loadedmetadata', onMeta);
    if (element.readyState >= 1) onMeta();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      element.removeEventListener('seeked', onSeeked);
      element.removeEventListener('play', onPlay);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('loadedmetadata', onMeta);
    };
  }, [element, goTo]);

  // When the list changes (a cut, a speed change), stay at the same output time, inside the result.
  useEffect(() => {
    if (!element || !ready) return;
    goTo(outputRef.current, element);
  }, [element, goTo, placements, ready]);

  const seek = useCallback((target: number) => {
    if (element) goTo(target, element);
  }, [element, goTo]);

  const play = useCallback(() => {
    if (!element) return;
    wantPlayRef.current = true;
    const looping = loopRef.current;
    if (looping && (outputRef.current < looping.fromMs || outputRef.current >= looping.toMs - 1)) goTo(looping.fromMs, element);
    else if (outputRef.current >= durationMs - 1) goTo(0, element);
    if (holdRef.current) {
      holdRef.current = { startedAt: performance.now(), fromMs: outputRef.current };
      setPlaying(true);
      return;
    }
    const result = element.play() as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') result.catch(() => undefined);
  }, [durationMs, element, goTo]);
  const pause = useCallback(() => {
    wantPlayRef.current = false;
    if (holdRef.current) setPlaying(false);
    element?.pause();
  }, [element]);
  const toggle = useCallback(() => (playing ? pause() : play()), [pause, play, playing]);

  const setRate = useCallback((next: number) => {
    const clamped = Math.max(0.25, Math.min(2, next));
    rateRef.current = clamped;
    setRateState(clamped);
    if (element) {
      const segment = placementsRef.current[Math.min(indexRef.current, placementsRef.current.length - 1)]?.segment;
      if (segment) element.playbackRate = segment.speed * clamped;
    }
  }, [element]);
  const setLoop = useCallback((next: { fromMs: number; toMs: number } | null) => {
    const valid = next && next.toMs - next.fromMs >= 100 ? next : null;
    loopRef.current = valid;
    setLoopState(valid);
  }, []);

  return {
    ref, element, outputMs, durationMs, playing, ready, play, pause, toggle, seek,
    videoWidth: size.width, videoHeight: size.height, sourceMs, segmentIndex, holding, rate, setRate, loop, setLoop,
  };
}
